import { Head, Link, router } from '@inertiajs/react'
import { PageHeader, PageContent } from '@/components/PageHeader'
import { FinancialYearSelect } from '@/components/FinancialYearSelect'
import { TransactionsTable } from '@/components/transactions/TransactionsTable'
import { NewTransactionForm } from '@/components/transactions/NewTransactionForm'
import { ReceiptDropZone } from '@/components/transactions/ReceiptDropZone'
import { ReceiptConfirmDialog } from '@/components/transactions/ReceiptConfirmDialog'
import { Pagination } from '@/components/Pagination'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import type { Transaction, Category, Account, Pagination as PaginationType } from '@/types'
import { UploadIcon, AlertCircleIcon, SparklesIcon, LoaderIcon, SearchIcon, XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

interface Props {
  transactions: Transaction[]
  categories: Category[]
  account: Account | null
  filter: string | null
  q: string
  category_filter: number | null
  vendors: string[]
  costs_month: string | null
  vendor_filter: string | null
  transactions_stale: boolean
  accounts: Account[]
  reimbursements_account_id: number
  pagination: PaginationType
}

type ReceiptData = {
  transaction: {
    unique_reference: string
    account_id: number
    date: string
    memo: string
    amount: number
    amount_currency: string
    category_id: number | null
    has_tax: boolean
  }
  receipt_signed_id: string
  categories: { id: number; name: string; kind: string }[]
  preview_url: string
}

export default function Index({
  transactions,
  categories,
  account,
  filter,
  q,
  vendor_filter,
  vendors,
  category_filter,
  costs_month,
  transactions_stale,
  accounts,
  reimbursements_account_id,
  pagination,
}: Props) {
  const [receiptData, setReceiptData] = useState<ReceiptData | null>(null)
  const [categorizing, setCategorizing] = useState(false)
  const [searchQuery, setSearchQuery] = useState(q)
  const canManuallyAddTransaction = account?.supports_manual_entry && account.id !== reimbursements_account_id

  useEffect(() => {
    setSearchQuery(q)
  }, [q])

  function handleSearchSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const nextQuery = searchQuery.trim()
    if (nextQuery === q) return

    applySearch(nextQuery)
  }

  function handleClearSearch() {
    setSearchQuery('')
    if (q) applySearch('')
  }

  function applySearch(nextQuery: string) {
    const params = new URLSearchParams(window.location.search)
    params.delete('page')

    if (nextQuery) {
      params.set('q', nextQuery)
    } else {
      params.delete('q')
    }

    router.get('/transactions', Object.fromEntries(params), {
      preserveState: true,
      preserveScroll: true,
      replace: true,
      only: ['transactions', 'q', 'filter', 'pagination', 'transactions_stale'],
    })
  }

  function handleCategorize() {
    if (!account) return

    setCategorizing(true)
    router.post('/transactions/categorize', {
      account_id: account.id,
    }, {
      onFinish: () => setCategorizing(false),
    })
  }

  const buildFilterUrl = (filterKey: string | null) => {
    const params = new URLSearchParams(window.location.search)
    params.delete('page')

    if (account?.id) {
      params.set('account_id', account.id.toString())
    }
    if (filterKey) {
      params.set('filter', filterKey)
      if (filterKey === 'no_vendor') params.delete('vendor')
    } else {
      params.delete('filter')
    }
    const queryString = params.toString()
    return queryString ? `/transactions?${queryString}` : '/transactions'
  }

  const buildAccountUrl = (accountId: number | null) => {
    const params = new URLSearchParams(window.location.search)
    if (accountId === null) {
      params.set('account_id', 'all')
    } else {
      params.set('account_id', accountId.toString())
    }
    params.delete('page')

    const queryString = params.toString()
    return queryString ? `/transactions?${queryString}` : '/transactions'
  }

  const isUpsiderAccount = account?.type === 'Account::Upsider'
  const paginationParams = (() => {
    const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search)
    params.delete('page')
    return Object.fromEntries(params.entries())
  })()
  const filterTabs = [
    { key: null, label: 'All' },
    { key: 'uncategorized', label: 'Uncategorized' },
    { key: 'no_vendor', label: 'No Vendor' },
    ...(isUpsiderAccount ? [] : [{ key: 'no_receipts', label: 'No Receipts' }]),
  ]

  return (
    <>
      <Head title="Transactions" />

      <PageHeader title="Transactions" description="Manage and track all your financial transactions">
        <form onSubmit={handleSearchSubmit} className="relative w-full md:w-[280px]">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search transactions"
            className="h-8 pl-8 pr-8 text-[13px]"
            aria-label="Search transactions"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-2 top-1/2 rounded p-0.5 text-muted-foreground transition-colors -translate-y-1/2 hover:bg-muted hover:text-foreground"
              aria-label="Clear search"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </form>
        <select aria-label="Filter by vendor" value={vendor_filter ?? ''}
          className="h-8 max-w-[220px] rounded-md border border-input bg-background px-2 text-[13px]"
          onChange={event => {
            const params = new URLSearchParams(window.location.search)
            if (event.target.value) { params.set('vendor', event.target.value); if (params.get('filter') === 'no_vendor') params.delete('filter') }
            else params.delete('vendor')
            params.delete('page')
            router.get('/transactions', Object.fromEntries(params), { preserveState: true, preserveScroll: true })
          }}>
          <option value="">All vendors</option>
          {[...new Set([...vendors, ...(vendor_filter ? [vendor_filter] : [])])].sort().map(vendor => <option key={vendor} value={vendor}>{vendor}</option>)}
        </select>
        <FinancialYearSelect />
        <Link href="/transactions/advanced">
          <Button
            variant={transactions_stale ? 'destructive' : 'outline'}
            size="sm"
            className="gap-1.5"
          >
            <UploadIcon className="h-3.5 w-3.5" />
            Import
          </Button>
        </Link>
      </PageHeader>

      <div className="border-b border-border bg-card px-4 md:px-8 py-3">
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex flex-wrap items-center gap-0.5 rounded-lg bg-muted p-0.5">
            <Link
              href={buildAccountUrl(null)}
              className={cn(
                'rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors',
                !account
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              All
            </Link>
            {accounts.map((item) => (
              <Link
                key={item.id}
                href={buildAccountUrl(item.id)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors',
                  account?.id === item.id
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {item.display_name}
              </Link>
            ))}
          </div>

          <div className="h-5 w-px bg-border" />

          <div className="flex items-center gap-0.5">
            {filterTabs.map((tab) => (
              <Link
                key={tab.key || 'all'}
                href={buildFilterUrl(tab.key)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors',
                  filter === tab.key
                    ? 'bg-primary/8 text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {tab.label}
              </Link>
            ))}
          </div>

          {account && filter === 'uncategorized' && transactions.length > 0 && (
            <>
              <div className="h-5 w-px bg-border" />
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={handleCategorize}
                disabled={categorizing}
              >
                {categorizing ? (
                  <LoaderIcon className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <SparklesIcon className="h-3.5 w-3.5" />
                )}
                {categorizing ? 'Categorizing...' : 'Categorize with AI'}
              </Button>
            </>
          )}
        </div>
      </div>

      {transactions_stale && (
        <div className="mx-4 md:mx-8 mt-4">
          <div className="flex items-center gap-3 rounded-lg border border-warning/20 bg-warning/5 px-4 py-2.5">
            <AlertCircleIcon className="h-4 w-4 text-warning-foreground" />
            <p className="text-[13px] text-warning-foreground">
              Your transactions may be outdated.{' '}
              <Link href="/transactions/advanced" className="font-medium underline underline-offset-2">
                Import new data
              </Link>
            </p>
          </div>
        </div>
      )}

      <PageContent>
        {category_filter && <div className="mb-3 text-sm">Category: {categories.find(category => category.id === category_filter)?.name ?? category_filter} <button className="ml-2 underline" onClick={() => { const params = new URLSearchParams(window.location.search); params.delete('category_id'); params.delete('page'); router.get('/transactions', Object.fromEntries(params)) }}>Clear category</button></div>}
        {costs_month && <div className="mb-3 text-sm">Month: {costs_month} <button className="ml-2 underline" onClick={() => { const params = new URLSearchParams(window.location.search); params.delete('costs_month'); params.delete('page'); router.get('/transactions', Object.fromEntries(params)) }}>Clear month</button></div>}
        {vendor_filter && <div className="mb-3 text-sm">Vendor: {vendor_filter} <button className="ml-2 underline" onClick={() => { const params = new URLSearchParams(window.location.search); params.delete('vendor'); params.delete('page'); router.get('/transactions', Object.fromEntries(params)) }}>Clear vendor</button></div>}
        {account?.supports_manual_entry && (
          <div className="mb-6 space-y-3">
            <ReceiptDropZone onReceiptProcessed={setReceiptData} />

            {canManuallyAddTransaction && (
              <Card>
                <CardContent className="p-4">
                  <NewTransactionForm account={account} categories={categories} />
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <Card>
          <CardContent className="p-0">
            <TransactionsTable
              transactions={transactions}
              categories={categories}
              account={account}
            />
            <Pagination
              pagination={pagination}
              baseUrl="/transactions"
              preserveParams={paginationParams}
            />
          </CardContent>
        </Card>
      </PageContent>

      <ReceiptConfirmDialog data={receiptData} onClose={() => setReceiptData(null)} />
    </>
  )
}
